import { useMutation, useQuery } from 'react-query';
import {
  cancelCaseById,
  deleteCaseById,
  getFundingSearchById,
  getProfileByFundingSearchId,
  setCasePrivacy,
  patchProfileData,
  getProfileSectionData,
  getProfileSectionJsonSchema,
  putProfileSectionData,
  postProfileLenderSelection,
  getProfileLenderSelection,
  getProfileDownloadAllDocuments,
  getProfileDocumentsUploaded,
  postProfileDocumentsUploaded,
  deleteProfileDocumentsUploaded,
  patchProfileDocumentsSetComplete,
  postDirectorSection,
  putDirectorSection,
  deleteDirectorSection,
  getProfileDocumentsRules,
  applyCaseById,
  getProfileByApplicationId,
  getProfileDownloadCsv,
  getProfileByEntityId,
  getFundingAffordabilityProfileByEntityId,
  postAffordabilityProfileByEntityId,
  patchAffordabilityProfileData,
  getMatchResultsAverageProfileId,
  postMaxBorrowAmount,
  postRepayments,
  getQuestionnaireEligibility,
} from '.';

export const useQueryGetFundingSearchProfile = (fundingSearchId) =>
  useQuery(
    ['funding-search-profile', fundingSearchId],
    async () => {
      const [fundingSearch, profile] = await Promise.all([
        getFundingSearchById(fundingSearchId),
        getProfileByFundingSearchId(fundingSearchId),
      ]);

      return {
        fundingSearch: fundingSearch?.data ?? {},
        profile: profile?.data ?? {},
      };
    },
    { enabled: !!fundingSearchId, retry: false },
  );

export const useQueryGetProfileByFundingSearchId = (fundingSearchId) =>
  useQuery(
    ['profile-funding-search-id', fundingSearchId],
    async () => {
      const { data } = await getProfileByFundingSearchId(fundingSearchId);
      return data;
    },
    { enabled: !!fundingSearchId, retry: false },
  );

export const useQueryGetProfileByApplicationId = (applicationId) =>
  useQuery(
    ['application-profile', applicationId],
    async () => {
      const { data } = await getProfileByApplicationId(applicationId);
      return data;
    },
    { enabled: !!applicationId, retry: false },
  );

export const useQueryGetFundingSearchProfileSection = (fundingSearchId, section) =>
  useQuery(
    ['funding-search-profile-section', fundingSearchId, section],
    async () => {
      const [profile, jsonSchema] = await Promise.all([
        getProfileSectionData(fundingSearchId, section),
        getProfileSectionJsonSchema(section),
      ]);

      return {
        profile: profile?.data ?? {},
        jsonSchema: jsonSchema?.data ?? {},
      };
    },
    { enabled: !!fundingSearchId && !!section, retry: false /* staleTime: 1000 * 60 * 60 */ },
  );

export const useQueryGetDocuments = (profileId) =>
  useQuery(
    ['funding-search-profile-documents', profileId],
    async () => {
      const [documents, uploadedDocuments] = await Promise.all([
        getProfileDocumentsRules(profileId),
        getProfileDocumentsUploaded(profileId),
      ]);
      const documentsList = documents?.data ?? [];
      return documentsList.map((document) => {
        const files = uploadedDocuments?.data
          ?.filter((file) => file.group === document.group)
          ?.map((file) => ({
            file: {
              ...file,
              name: file.fileName,
            },
            progress: 100,
          }));
        return {
          ...document,
          files: files ?? [],
        };
      });
    },
    { enabled: !!profileId, retry: false },
  );

export const useQueryGetDownloadAllDocuments = (profileId, enabled = true) =>
  useQuery(
    ['funding-search-download-all-documents', profileId],
    async () => {
      const response = await getProfileDownloadAllDocuments(profileId);
      return response?.data?.url;
    },
    { enabled: enabled && !!profileId, retry: false },
  );

export const useMutationGetDownloadProfileCsv = () =>
  useMutation(async (profileId) => {
    const response = await getProfileDownloadCsv(profileId);
    const data = response?.data ?? {};
    return data;
  });

export const useQueryGetFundingSearchProfileOnlySection = (fundingSearchId, section) =>
  useQuery(
    ['funding-search-profile-section', fundingSearchId, section],
    async () => {
      const profile = await getProfileSectionData(fundingSearchId, section);
      return profile?.data ?? {};
    },
    { enabled: !!fundingSearchId && !!section, retry: false /* staleTime: 1000 * 60 * 60 */ },
  );

export const useQueryGetProfileLenderSelection = (id, enabled = true) =>
  useQuery(
    ['profile-lender-selection'],
    async () => {
      const lenders = await getProfileLenderSelection(id);
      const lendersData = lenders?.data ?? [];
      return lendersData;
    },
    { enabled: !!enabled, retry: false },
  );

export const useQueryGetProfileByEntityId = (id) =>
  useQuery(
    ['profile-entity-id', id],
    async () => {
      const profile = await getProfileByEntityId(id);
      return profile?.data ?? null;
    },
    { enabled: !!id, retry: false, staleTime: 1000 * 60 * 60 },
  );

export const useMutationPutProfileSection = () => {
  return useMutation(async ({ id, section, payload }) => {
    await putProfileSectionData(id, section, payload);
    return;
  });
};
export const useMutationPostDirectorSection = () => {
  return useMutation(async ({ id, payload }) => {
    await postDirectorSection(id, payload);
    return;
  });
};
export const useMutationPutDirectorSection = () => {
  return useMutation(async ({ id, directorId, payload }) => {
    await putDirectorSection(id, directorId, payload);
    return;
  });
};
export const useMutationDeleteDirectorSection = () => {
  return useMutation(async ({ id, directorId }) => {
    await deleteDirectorSection(id, directorId);
    return;
  });
};
export const useMutationPostProfileDocument = (options) => {
  return useMutation(async ({ profileId, id, group, file, progressCallback }) => {
    const result = await postProfileDocumentsUploaded({
      profileId,
      group,
      file,
      progressCallback,
      id,
    });
    return { id: result?.data };
  }, options);
};
export const useMutationDeleteProfileDocument = (options) => {
  return useMutation(async ({ profileId, id }) => {
    await deleteProfileDocumentsUploaded(profileId, id);
    return;
  }, options);
};
export const useMutationPatchProfileDocumentSetComplete = () => {
  return useMutation(async ({ id }) => {
    await patchProfileDocumentsSetComplete(id);
    return;
  });
};
export const useMutationPatchProfile = () => {
  return useMutation(async ({ id, payload }) => {
    await patchProfileData(id, payload);
    return;
  });
};

export const useMutationNewPatchProfile = () => {
  return useMutation(async ({ id, payload }) => {
    await patchAffordabilityProfileData(id, payload);
    return;
  });
};

export const useMutationPostProfileLenderSelection = () => {
  return useMutation(async ({ id, payload }) => {
    await postProfileLenderSelection(id, payload);
    return;
  });
};

export const useMutationDeleteCase = () => {
  return useMutation(async (id) => {
    const result = await deleteCaseById(id);
    return result?.data ?? {};
  });
};

export const useMutationCancelCase = () => {
  return useMutation(async (id) => {
    const result = await cancelCaseById(id);
    const data = result?.data ?? {};
    return data;
  });
};

export const useMutationApplyCase = () => {
  return useMutation(async (id) => {
    const result = await applyCaseById(id);
    const data = result?.data ?? {};
    return data;
  });
};

export const useMutationCasePrivacy = () => {
  return useMutation(async ({ id, payload }) => {
    const result = await setCasePrivacy(id, payload);
    const data = result?.data ?? {};
    return data;
  });
};

export const useQueryGetFundingAffordabilityProfileByEntityId = (id) =>
  useQuery(
    ['funding-affordability-profile-entity-id', id],
    async () => {
      const profile = await getFundingAffordabilityProfileByEntityId(id);

      return profile?.data ?? null;
    },
    {
      enabled: !!id,
      retry: false,
      staleTime: null,
    },
  );

export const useMutationCreateAffordabilityProfileByEntityId = () => {
  return useMutation(async ({ id, payload }) => {
    const result = await postAffordabilityProfileByEntityId(id, payload);
    const data = result?.data ?? {};
    return data;
  });
};

export const useMutationPatchProfileAffordability = () => {
  return useMutation(async ({ id, payload }) => {
    const response = await patchAffordabilityProfileData(id, payload);
    return response.data ?? {};
  });
};

export const useMutationPostMaxBorrowAmount = () => {
  return useMutation(async ({ payload }) => {
    const response = await postMaxBorrowAmount(payload);
    return response.data ?? {};
  });
};

export const useMutationPostRepayments = () => {
  return useMutation(async ({ payload }) => {
    const response = await postRepayments(payload);
    return response.data ?? {};
  });
};

export const useQueryGetMatchResultsAverage = (id) =>
  useQuery(
    ['matches-results-average-profile-id', id],
    async () => {
      const profile = await getMatchResultsAverageProfileId(id);

      return profile?.data ?? null;
    },
    {
      enabled: !!id,
      retry: false,
      staleTime: null,
    },
  );

export const useQueryGetQuestionnaireEligibility = (questionnaireType, entityId) =>
  useQuery(
    ['questionnaire-eligibility', questionnaireType, entityId],
    async () => {
      const eligibility = await getQuestionnaireEligibility(questionnaireType, entityId);
      return eligibility?.data ?? null;
    },
    { enabled: !!entityId, retry: false, staleTime: 1000 * 60 * 60 },
  );
